import type {
  QuizDetail,
  QuizUserLite,
  QuizResults,
  QuizTeabateSelection,
  QuizTeabateStance,
  QuizViewerState,
} from "types/quiz";
import type { QuizUiStrings } from "i18n/quizUi";
import { buildShareRouteUrl, buildQuizSharePath } from "util/shareUrls";
import type { User } from "util/constants";

export interface QuizAlignmentStatement {
  id: number;
  statement: string;
  category: string;
  reverse: boolean;
}

const QUIZ_DIMENSION_METADATA: Record<string, { category: string; reverse: boolean }> = {
  nationalism: { category: "nationalism_cosmopolitanism", reverse: true },
  cosmopolitanism: { category: "nationalism_cosmopolitanism", reverse: false },
  theocracy: { category: "theocracy_secularism", reverse: true },
  secularism: { category: "theocracy_secularism", reverse: false },
  authority: { category: "authority_liberty", reverse: true },
  liberty: { category: "authority_liberty", reverse: false },
  capitalism: { category: "capitalism_socialism", reverse: true },
  socialism: { category: "capitalism_socialism", reverse: false },
  dogma: { category: "dogma_science", reverse: true },
  science: { category: "dogma_science", reverse: false },
  autocracy: { category: "autocracy_democracy", reverse: true },
  democracy: { category: "autocracy_democracy", reverse: false },
  conservatism: { category: "conservatism_progressivism", reverse: true },
  progressivism: { category: "conservatism_progressivism", reverse: false },
  content: { category: "content_discontent", reverse: true },
  discontent: { category: "content_discontent", reverse: false },
};

export const QUIZ_DIMENSION_VALUES = [
  "nationalism",
  "cosmopolitanism",
  "theocracy",
  "secularism",
  "authority",
  "liberty",
  "capitalism",
  "socialism",
  "dogma",
  "science",
  "autocracy",
  "democracy",
  "conservatism",
  "progressivism",
  "content",
  "discontent",
] as const;

/**
 * Replace `{placeholder}` tokens in a localized template string.
 */
export function formatQuizTemplate(template: string, values: Record<string, string | number>): string {
  return Object.entries(values).reduce(
    (result, [key, value]) => result.replace(new RegExp(`\\{${key}\\}`, "g"), String(value)),
    template
  );
}

/**
 * Build a shareable in-app quiz URL from the current origin.
 */
export function buildQuizShareUrl(code: string): string {
  return buildShareRouteUrl(buildQuizSharePath(code));
}

/**
 * Return the normalized dimension metadata used by the quiz alignment view.
 */
export function getQuizDimensionMetadata(dimension: string | null | undefined): {
  category: string;
  reverse: boolean;
} | null {
  const normalized = String(dimension || "").trim().toLowerCase();
  return QUIZ_DIMENSION_METADATA[normalized] || null;
}

/**
 * Resolve a localized label for one quiz dimension value.
 */
export function getQuizDimensionLabel(
  dimension: (typeof QUIZ_DIMENSION_VALUES)[number] | string,
  ui: QuizUiStrings
): string {
  switch (dimension) {
    case "nationalism":
      return ui.dimensionNationalism;
    case "cosmopolitanism":
      return ui.dimensionCosmopolitanism;
    case "theocracy":
      return ui.dimensionTheocracy;
    case "secularism":
      return ui.dimensionSecularism;
    case "authority":
      return ui.dimensionAuthority;
    case "liberty":
      return ui.dimensionLiberty;
    case "capitalism":
      return ui.dimensionCapitalism;
    case "socialism":
      return ui.dimensionSocialism;
    case "dogma":
      return ui.dimensionDogma;
    case "science":
      return ui.dimensionScience;
    case "autocracy":
      return ui.dimensionAutocracy;
    case "democracy":
      return ui.dimensionDemocracy;
    case "conservatism":
      return ui.dimensionConservatism;
    case "progressivism":
      return ui.dimensionProgressivism;
    case "content":
      return ui.dimensionContent;
    case "discontent":
      return ui.dimensionDiscontent;
    default:
      return String(dimension);
  }
}

/**
 * Convert selected quiz teabates into AlignmentSummaryTile statements.
 */
export function buildQuizAlignmentStatements(teabates: QuizTeabateSelection[]): QuizAlignmentStatement[] {
  return teabates
    .map((teabate) => {
      const dimension = String(teabate.post.properties?.dimension || "").trim().toLowerCase();
      const metadata = getQuizDimensionMetadata(dimension);
      if (!metadata || !teabate.id) {
        return null;
      }
      return {
        id: teabate.id,
        statement: teabate.post.title || "",
        category: metadata.category,
        reverse: metadata.reverse,
      };
    })
    .filter((value): value is QuizAlignmentStatement => value !== null);
}

/**
 * Look up one stance by quiz-teabate id.
 */
export function getQuizStanceByTeabateId(
  stances: QuizTeabateStance[],
  quizTeabateId: number
): QuizTeabateStance | null {
  return stances.find((stance) => stance.quiz_teabate_id === quizTeabateId) || null;
}

/**
 * Return the viewer state from a detail or results payload.
 */
export function getQuizViewerState(source: QuizDetail | QuizResults): QuizViewerState | QuizResults["viewer"] | null {
  return source.viewer || null;
}

/**
 * Return the active results participant for one user id.
 */
export function getResultParticipantByUserId(results: QuizResults | null, userId: number): QuizResults["participants"][number] | null {
  if (!results) {
    return null;
  }
  return results.participants.find((participant) => participant.user_id === userId) || null;
}

/**
 * Resolve the shared primary/secondary button classes used across quiz screens.
 */
export function getQuizButtonClassName({
  variant = "primary",
  disabled = false,
  fullWidth = false,
}: {
  variant?: "primary" | "secondary";
  disabled?: boolean;
  fullWidth?: boolean;
}): string {
  const baseClassName = variant === "secondary"
    ? "btn-secondary"
    : disabled
      ? "btn-primary-disabled"
      : "btn-primary";

  return `${baseClassName} inline-flex min-h-[44px] items-center justify-center gap-2 px-4 py-2 ${
    fullWidth ? "w-full" : ""
  } ${disabled ? "cursor-not-allowed opacity-60" : ""}`.trim();
}

/**
 * Adapt lightweight quiz user payloads to the broader legacy `User` shape that
 * `EntityAvatar` already knows how to render consistently.
 */
export function mapQuizUserToEntityAvatarUser(user: QuizUserLite | null | undefined): User | undefined {
  if (!user || typeof user.username !== "string" || user.username.trim().length === 0 || !user.id) {
    return undefined;
  }

  const propertiesPublic = user.properties_public && typeof user.properties_public === "object"
    ? user.properties_public
    : null;

  // We intentionally drop `avatar_url` here. Avatars are always
  // displayed via `IMAGE_CDN + avatar_key` (see `util/actorIdentity`),
  // and the legacy `avatar_url` field is a raw S3 location that
  // should never be served to the browser.
  const avatarKey = typeof propertiesPublic?.avatar_key === "string" ? propertiesPublic.avatar_key : "";
  const isPrivate = typeof propertiesPublic?.is_private === "boolean" ? propertiesPublic.is_private : undefined;

  return {
    id: user.id,
    id_short: String(user.id),
    username: user.username,
    login_name: user.username,
    display_name: user.display_name || user.username,
    email: "",
    password_weak: null,
    password_last_set_ts: "",
    created_at: "",
    updated_at: "",
    is_active: true,
    is_admin: false,
    auth_type: "",
    auth_connections: [],
    has_password: false,
    last_login_at: "",
    deactivated_at: "",
    deleted_at: "",
    de_reason: null,
    properties: {},
    about: {},
    properties_public: {
      avatar_url: "",
      avatar_key: avatarKey,
      creator_id: 0,
      ...(isPrivate !== undefined ? { is_private: isPrivate } : {}),
    },
  };
}
